import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getSupabase, isSupabaseConfigured } from "./supabase";
import { defaultContent, isServiceIcon, type SiteContent } from "./siteContent";

interface ContentContextValue {
  content: SiteContent;
  loading: boolean;
  missingTable: boolean;
  refresh: () => Promise<void>;
  save: (next: SiteContent) => Promise<string | null>;
}

const ContentContext = createContext<ContentContextValue | null>(null);

function normalize(raw: SiteContent | null | undefined): SiteContent {
  if (!raw) return defaultContent;
  return {
    hero: { ...defaultContent.hero, ...raw.hero },
    services: {
      ...defaultContent.services,
      ...raw.services,
      items: (raw.services?.items ?? defaultContent.services.items).map((item) => ({
        ...item,
        icon: isServiceIcon(item.icon) ? item.icon : "cpu",
      })),
    },
    howItWorks: {
      ...defaultContent.howItWorks,
      ...raw.howItWorks,
      items: raw.howItWorks?.items ?? defaultContent.howItWorks.items,
    },
    contact: { ...defaultContent.contact, ...raw.contact },
  };
}

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContent>(defaultContent);
  const [loading, setLoading] = useState(true);
  const [missingTable, setMissingTable] = useState(false);

  const refresh = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    const { data, error } = await getSupabase()
      .from("site_content")
      .select("hero, services, how_it_works, contact")
      .eq("id", "main")
      .maybeSingle();

    if (error) {
      setMissingTable(true);
      setLoading(false);
      return;
    }
    setMissingTable(false);
    if (data) {
      setContent(
        normalize({
          hero: data.hero,
          services: data.services,
          howItWorks: data.how_it_works,
          contact: data.contact,
        }),
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(async (next: SiteContent) => {
    if (!isSupabaseConfigured) {
      return "Supabase não está configurado.";
    }
    const payload = {
      id: "main",
      hero: next.hero,
      services: next.services,
      how_it_works: next.howItWorks,
      contact: next.contact,
      updated_at: new Date().toISOString(),
    };
    const { error } = await getSupabase().from("site_content").upsert(payload, { onConflict: "id" });
    if (error) {
      return error.message.includes("schema cache") || error.message.includes("does not exist")
        ? "Falta criar a tabela site_content. Corre o SQL em supabase/migrations/002_site_content.sql."
        : "Não foi possível guardar. Tenta novamente.";
    }
    setContent(next);
    setMissingTable(false);
    return null;
  }, []);

  const value = useMemo(
    () => ({ content, loading, missingTable, refresh, save }),
    [content, loading, missingTable, refresh, save],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useSiteContent(): ContentContextValue {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useSiteContent deve ser usado dentro de ContentProvider");
  return ctx;
}
